/**
 * Song List Component - Displays and manages songs
 */
import React, { useState, useEffect } from 'react';
import { useAudio } from '../contexts/AudioContext';
import { songsAPI, favoritesAPI } from '../services/api';
import { formatRelativeTime } from '../utils/timeFormat';
import YouTubeEmbed from './YouTubeEmbed';
import './SongList.css';

const SongList = ({ search = '', status = '', favoritesOnly = false }) => {
  const { currentSong, isPlaying, playSong, playlist, setPlaylist } = useAudio();
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [embedSong, setEmbedSong] = useState(null);
  const [embedIndex, setEmbedIndex] = useState(0);

  // Handle custom event for YouTube embed navigation
  useEffect(() => {
    const handlePlayYouTubeEmbed = (event) => {
      const { song, index } = event.detail;
      setEmbedSong(song);
      setEmbedIndex(index);
    };

    window.addEventListener('playYouTubeEmbed', handlePlayYouTubeEmbed);
    return () => {
      window.removeEventListener('playYouTubeEmbed', handlePlayYouTubeEmbed);
    };
  }, []);

  // Fetch songs when component mounts or filters change
  useEffect(() => {
    fetchSongs();
  }, [search, status, favoritesOnly]);

  const fetchSongs = async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (status) params.status = status;
      if (favoritesOnly) params.favorites = 'true';

      const response = await songsAPI.getSongs(params);
      setSongs(response.data.songs);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch songs:', err);
      setError('Failed to load songs');
    } finally {
      setLoading(false);
    }
  };

  const handlePlaySong = (song, index) => {
    // For pending, failed, or downloading songs, show YouTube embed
    if (song.download_status !== 'completed') {
      setEmbedSong(song);
      setEmbedIndex(index);
      return;
    }

    // For completed songs, use normal audio player
    // Update playlist if needed
    if (playlist.length !== songs.length || playlist.some((s, i) => s.id !== songs[i]?.id)) {
      setPlaylist(songs);
    }
    playSong(song, songs, index);
  };

  const handleToggleFavorite = async (e, songId) => {
    e.stopPropagation(); // Prevent playing the song when clicking favorite
    try {
      const song = songs.find(s => s.id === songId);
      if (song.is_favorite) {
        await favoritesAPI.removeFavorite(songId);
      } else {
        await favoritesAPI.addFavorite(songId);
      }
      // Refresh the song list to update favorite status
      await fetchSongs();
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
    }
  };

  const handleDeleteSong = async (e, songId) => {
    e.stopPropagation(); // Prevent playing the song when clicking delete
    if (!window.confirm('Are you sure you want to delete this song?')) return;

    try {
      setDeletingId(songId);
      await songsAPI.deleteSong(songId);
      // Remove from local state
      setSongs(songs.filter(s => s.id !== songId));
    } catch (err) {
      console.error('Failed to delete song:', err);
      alert('Failed to delete song');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSongUpdated = (updatedSong) => {
    // Update the song in the local state when metadata is refreshed
    setSongs(prevSongs =>
      prevSongs.map(song =>
        song.id === updatedSong.id ? { ...song, ...updatedSong } : song
      )
    );
  };

  const getStatusBadge = (song) => {
    switch (song.download_status) {
      case 'pending':
        return <span className="status-badge pending">Pending</span>;
      case 'downloading':
        return <span className="status-badge downloading">Downloading</span>;
      case 'completed':
        return null;
      case 'failed':
        return <span className="status-badge failed">Failed</span>;
      default:
        return null;
    }
  };

  const extractVideoId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/,
      /^youtube\s+([a-zA-Z0-9_-]+)/i,
      /^([a-zA-Z0-9_-]{11})$/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const formatSongTitle = (song) => {
    const videoId = extractVideoId(song.youtube_url);
    const isUnknown = song.title === 'Unknown Title' || !song.title;

    if (isUnknown && videoId) {
      return (
        <div className="song-title-wrapper">
          <span className="song-title-text">YouTube: {videoId}</span>
          <a
            href={song.youtube_url}
            target="_blank"
            rel="noopener noreferrer"
            className="youtube-link"
            title="Open on YouTube"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
              <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/>
            </svg>
          </a>
        </div>
      );
    }

    return song.title;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="song-list loading">
        <div className="spinner" />
        <p>Loading songs...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="song-list error">
        <p>{error}</p>
        <button onClick={fetchSongs} className="btn-retry">Retry</button>
      </div>
    );
  }

  if (songs.length === 0) {
    return (
      <div className="song-list empty">
        <p>No songs found</p>
        {search && <p>Try adjusting your search or filters</p>}
      </div>
    );
  }

  return (
    <div className="song-list">
      <div className="song-list-header">
        <div className="header-play">Status</div>
        <div className="header-title">Title</div>
        <div className="header-artist">Artist</div>
        <div className="header-duration">Duration</div>
        <div className="header-time">Last Played</div>
        <div className="header-actions">Actions</div>
      </div>

      {/* Add click hint for failed songs */}
      {songs.some(s => s.download_status !== 'completed') && (
        <div className="click-hint">
          🎵 Click any song to play via YouTube embed
        </div>
      )}

      <div className="song-list-body">
        {songs.map((song, index) => (
          <div
            key={song.id}
            className={`song-item ${currentSong?.id === song.id ? 'playing' : ''} ${song.download_status !== 'completed' ? 'embed-clickable' : ''}`}
            onClick={() => handlePlaySong(song, index)}
            title={song.download_status !== 'completed' ? 'Click to play via YouTube embed' : 'Click to play'}
          >
            <div className="song-status">
              {currentSong?.id === song.id && isPlaying ? (
                <div className="playing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              ) : (
                getStatusBadge(song)
              )}
            </div>

            <div className="song-title">
              {formatSongTitle(song)}
              {song.error_message && (
                <div className="error-message" title={song.error_message}>
                  Download failed
                </div>
              )}
            </div>

            <div className="song-artist">{song.artist || 'Unknown Artist'}</div>

            <div className="song-duration">
              {formatDuration(song.duration)}
            </div>

            <div className="song-time">
              {song.last_listened_at ? formatRelativeTime(song.last_listened_at) : 'Never'}
            </div>

            <div className="song-actions">
              <button
                className={`btn-favorite ${song.is_favorite ? 'active' : ''}`}
                onClick={(e) => handleToggleFavorite(e, song.id)}
                title={song.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
              >
                <svg viewBox="0 0 24 24" fill={song.is_favorite ? 'currentColor' : 'none'} stroke="currentColor">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
              </button>

              <button
                className="btn-delete"
                onClick={(e) => handleDeleteSong(e, song.id)}
                title="Delete song"
                disabled={deletingId === song.id}
              >
                {deletingId === song.id ? (
                  <div className="spinner-small" />
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6"/>
                  </svg>
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      {embedSong && (
        <YouTubeEmbed
          song={embedSong}
          onClose={() => setEmbedSong(null)}
          autoplay={true}
          allSongs={songs}
          currentIndex={embedIndex}
          onSongUpdated={handleSongUpdated}
        />
      )}
    </div>
  );
};

export default SongList;